/**
 * Trunnion AI - Export Workflow Dialog
 */

import { useState, useMemo } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { CodeBlock } from '@/components/ui/CodeBlock';
import { useWorkflowDesignerStore } from '@/stores/workflowDesignerStore';
import { exportWorkflow, createExportBlob, downloadFile } from '@/lib/serialization';
import type { WorkflowDefinition } from '@/types';
import { Download } from 'lucide-react';

interface ExportDialogProps {
  open: boolean;
  onClose: () => void;
}

export function ExportDialog({ open, onClose }: ExportDialogProps) {
  const [format, setFormat] = useState<'json' | 'yaml'>('yaml');

  const {
    workflowId,
    workflowName,
    workflowDescription,
    workflowClassification,
    workflowVersion,
    nodes,
    edges,
    viewport,
  } = useWorkflowDesignerStore();

  const workflow: WorkflowDefinition = useMemo(
    () => ({
      id: workflowId ?? 'draft',
      name: workflowName,
      description: workflowDescription,
      classification: workflowClassification,
      version: workflowVersion,
      nodes: nodes.map((n) => ({
        id: n.id,
        type: n.type as WorkflowDefinition['nodes'][0]['type'],
        position: n.position,
        data: n.data,
      })),
      edges: edges.map((e) => ({
        id: e.id,
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle ?? undefined,
        targetHandle: e.targetHandle ?? undefined,
        type: (e.type ?? 'dataFlow') as 'dataFlow' | 'conditional',
        label: typeof e.label === 'string' ? e.label : undefined,
        data: e.data,
      })),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: 'current-user',
      isActive: true,
      tags: [],
      metadata: {},
      viewport,
    }),
    [
      workflowId,
      workflowName,
      workflowDescription,
      workflowClassification,
      workflowVersion,
      nodes,
      edges,
      viewport,
    ],
  );

  const content = useMemo(() => exportWorkflow(workflow, format), [workflow, format]);

  const handleDownload = () => {
    const blob = createExportBlob(content, format);
    const filename = `${workflowName.toLowerCase().replace(/\s+/g, '-')}.${format === 'yaml' ? 'yaml' : 'json'}`;
    downloadFile(blob, filename);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Export Workflow"
      size="xl"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button icon={<Download className="h-4 w-4" />} onClick={handleDownload}>
            Download
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex gap-2">
          <button
            onClick={() => setFormat('yaml')}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              format === 'yaml'
                ? 'bg-trunnion-gold-500/20 text-trunnion-gold-500'
                : 'text-trunnion-slate-400 hover:text-white'
            }`}
          >
            YAML
          </button>
          <button
            onClick={() => setFormat('json')}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              format === 'json'
                ? 'bg-trunnion-gold-500/20 text-trunnion-gold-500'
                : 'text-trunnion-slate-400 hover:text-white'
            }`}
          >
            JSON
          </button>
        </div>
        <CodeBlock
          content={content}
          language={format}
          title={`${workflowName}.${format}`}
          maxHeight="400px"
        />
      </div>
    </Modal>
  );
}
